import React from 'react';
import { Fragment } from 'react';
import { format, parseISO, isValid } from 'date-fns';
import useTranslation from 'next-translate/useTranslation';
import SkeletonLoader from './SkeletonLoader';
import AffiliateLink from './AffiliateLink';

// Helper to format ISO datetime to HH.mm (da-DK style, matches the old
// toLocaleTimeString output)
// format() THROWS RangeError on an invalid date, where the old
// toLocaleTimeString just rendered "Invalid Date". The CPH feed is not ours,
// so one malformed timestamp would take down the entire table on /departures,
// /arrivals and the homepage. Guard here, once, since every caller routes
// through this.
const formatTime = (isoString) => {
    if (!isoString) return '';
    const parsed = parseISO(isoString);
    return isValid(parsed) ? format(parsed, 'HH.mm') : '';
};

const FlightTable = ({ flights }) => {
    const { t } = useTranslation('common');

    // Helper function to track flight clicks with event properties
    const trackFlightClick = (flight, device) => {
        if (typeof umami !== 'undefined') {
            umami.track('flight-click', {
                destination: flight.Destination,
                iata: flight.Iata,
                airline: flight.Airline || flight.AirlineMobile,
                device: device,
                delayMinutes: calculateDelay(flight.ScheduledDateTime, flight.ExpectedDateTime)
            });
        }
    };

    // Calculate delay in minutes
    const calculateDelay = (scheduledDateTime, expectedDateTime) => {
        if (!scheduledDateTime || !expectedDateTime) return 0;
        const scheduled = parseISO(scheduledDateTime);
        const expected = parseISO(expectedDateTime);
        const delay = Math.round((expected - scheduled) / 60000);
        return Number.isNaN(delay) ? 0 : delay;
    };

    // if flights is a string, return nothing
    if (!flights || typeof flights === 'string') {
        return SkeletonLoader({ rows: 8 });
    }
    if (flights.length === 0)
        return (
            <div className="flex grow mb-10 justify-center">
                <div className="delayed-flights-table mt-10 text-left text-black bg-neutral-100 p-5 rounded w-full max-w-screen-lg overflow-y-auto h-fit max-h-[50vh]">
                    <div className="font-semibold text-center">
                        {t('common:no_delays')}
                    </div>
                </div>
            </div>
        );
    return (
        // data-nosnippet: keeps stale flight rows ("Delta Air Lines 00.00") out
        // of Google snippets; does not affect indexing, ranking or AIO for the
        // wait-time sentence (research log R1.4 only protects that sentence)
        <div
            data-nosnippet
            className="delayed-flights-table my-10 text-left text-black bg-neutral-100 p-5 rounded w-[95%] max-w-screen-lg overflow-hidden m-auto"
        >
            <div className="font-semibold mb-3 text-sm md:text-base">
                {t('common:delays_with_12_hours')}
            </div>

            <div className="overflow-x-hidden">
                <table className="w-full border-separate border-spacing-x-0 border-spacing-y-2">
                    <thead className="sr-only">
                        <tr>
                            <th scope="col">{t('common:departure_time')}</th>
                            <th scope="col">{t('common:destination')}</th>
                            <th scope="col" className="hidden md:block">
                                {t('common:airline')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {flights.map((flight, index) => {
                            const [place, airport] =
                                flight.DestinationName.split('-');
                            return (
                                <Fragment key={`flight-rows-${index}`}>
                                    <tr
                                        key={`desktop-${index}`}
                                        className="hidden md:table-row bg-white rounded my-2 drop-shadow cursor-pointer hover:bg-neutral-200 transition-all"
                                        onClick={() => {
                                            trackFlightClick(flight, 'desktop');
                                            window.open(
                                                `https://www.cph.dk/flyinformation/afgange/${flight.Iata}?id=${flight.FlightId}`,
                                                '_blank'
                                            );
                                        }}
                                        tabIndex="0" // Enable keyboard focus
                                        role="button" // Indicate interactive element
                                        aria-label={`Flight details for ${place} ${airport}, departing at ${formatTime(flight.ScheduledDateTime)} with ${flight.Airline}`}
                                    >
                                        <td className="p-4 px-8">
                                            <div className="font-light line-through whitespace-nowrap w-min">
                                                {formatTime(flight.ScheduledDateTime)}
                                            </div>
                                            <div className="font-semibold whitespace-nowrap w-min">
                                                {formatTime(flight.ExpectedDateTime)}
                                            </div>
                                        </td>

                                        <td className="p-4 px-8">
                                            <div className="font-semibold">
                                                {place} {airport}
                                            </div>
                                            <div>{flight.Iata}</div>
                                            {flight.Terminal && (
                                                <div className="text-sm opacity-60">
                                                    {flight.Terminal}
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-4 px-8 font-semibold">
                                            <div>{flight.Airline}</div>
                                        </td>
                                        <td className="p-4 pl-0">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-6 w-6 ml-auto"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M9 5l7 7-7 7"
                                                />
                                            </svg>
                                        </td>
                                    </tr>
                                    <tr
                                        key={`mobile-${index}`}
                                        className="text-sm md:hidden bg-white rounded my-2 drop-shadow cursor-pointer hover:bg-neutral-200 transition-all"
                                        onClick={() => {
                                            trackFlightClick(flight, 'mobile');
                                            window.open(
                                                `https://www.cph.dk/flyinformation/afgange/${flight.Iata}?id=${flight.FlightId}`,
                                                '_blank'
                                            );
                                        }}
                                    >
                                        <td className="p-2 px-4">
                                            <div className="font-light line-through whitespace-nowrap w-min">
                                                {formatTime(flight.ScheduledDateTime)}
                                            </div>
                                            <div className="font-semibold whitespace-nowrap w-min">
                                                {formatTime(flight.ExpectedDateTime)}
                                            </div>
                                        </td>

                                        <td className="py-2 mx-4 overflow-hidden max-w-[200px]">
                                            <div className="whitespace-nowrap w-min">
                                                <span className="font-semibold">
                                                    {flight.Destination + ' '}
                                                </span>
                                                ({flight.IataDestination})
                                            </div>
                                            <div className="whitespace-nowrap w-min">
                                                {flight.Iata}
                                            </div>
                                            <div className="whitespace-nowrap w-min">
                                                {flight.AirlineMobile}
                                            </div>
                                            {flight.Terminal && (
                                                <div className="whitespace-nowrap w-min text-xs opacity-60">
                                                    {flight.Terminal}
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-2 pl-0">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-6 w-6 ml-auto"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M9 5l7 7-7 7"
                                                />
                                            </svg>
                                        </td>
                                    </tr>
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
                <pre className="hidden">
                    {JSON.stringify(flights[0], null, 2)}
                </pre>
            </div>
            {/* EU261 kicks in at 3h; departure delay is a proxy for arrival delay */}
            {flights.some(
                (f) =>
                    calculateDelay(f.ScheduledDateTime, f.ExpectedDateTime) >=
                    180
            ) && (
                <div className="mt-4 text-center text-sm">
                    <AffiliateLink
                        type="text"
                        text={t('common:eu261_cta')}
                        umamiId="flighttable-eu261"
                        className="font-semibold underline"
                    />
                </div>
            )}
        </div>
    );
};

export default FlightTable;
